import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import * as echarts from 'echarts';

const RSSI_TICKS = [-90, -80, -70, -60, -50, -40, -30];
const BAR_COLOR = '#4B68CF';
const X_AXIS_MIN = -90;
const X_AXIS_MAX = -20;
const SLIDER_MIN = -90;
const SLIDER_MAX = -30;

// Each rssi label (e.g. -50) represents -41 ~ -50 dBm; bars span 10 axis units with no gap.
const getRssiDrawRange = rssi => [rssi, rssi + 10];
const getRssiLabelRange = rssi => [rssi, rssi + 9];

const parseRssiValue = rssi => {
  if (typeof rssi === 'number') return rssi;
  if (typeof rssi === 'string') {
    const matchedValue = rssi.match(/-?\d+/);
    return matchedValue ? Number(matchedValue[0]) : NaN;
  }
  return NaN;
};

export default function SignalQualityBarChart({ selectedIndex, setSelectedIndex, barChartData, targetThreshold }) {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  const safeData = useMemo(() => (Array.isArray(barChartData) ? barChartData : []), [barChartData]);
  const seriesData = useMemo(() => {
    const sortedData = [...safeData].sort((a, b) => parseRssiValue(a?.rssi) - parseRssiValue(b?.rssi));
    return sortedData
      .filter(item => Number.isFinite(parseRssiValue(item?.rssi)))
      .map(item => {
        const rssi = parseRssiValue(item?.rssi);
        const [rangeStart, rangeEnd] = getRssiDrawRange(rssi);
        const [, labelEnd] = getRssiLabelRange(rssi);
        return {
          value: [rangeStart, rangeEnd, item?.count ?? 0],
          labelEnd,
        };
      });
  }, [safeData]);
  const maxCount = useMemo(() => Math.max(...seriesData.map(item => item.value[2]), 0), [seriesData]);
  const isAllZero = maxCount === 0;
  const isAllZeroRef = useRef(isAllZero);
  isAllZeroRef.current = isAllZero;
  const markPointYGuess = useMemo(() => {
    if (maxCount <= 0) return 50;
    return Math.ceil(maxCount * 1.3);
  }, [maxCount]);

  // Y for markPoint must stay stable while dragging (slider only changes X). Reset when data / scale changes.
  const [axisTopForMark, setAxisTopForMark] = useState(markPointYGuess);
  useEffect(() => {
    setAxisTopForMark(markPointYGuess);
  }, [markPointYGuess]);
  const currentRssiText = useMemo(() => `${selectedIndex} dBm`, [selectedIndex]);

  const sliderFillStart = SLIDER_MAX > SLIDER_MIN ? ((SLIDER_MAX - selectedIndex) / (SLIDER_MAX - SLIDER_MIN)) * 100 : 0;

  const CHART_MARGIN = { left: 40, right: 40 };

  const option = useMemo(() => {
    const renderItem = (params, api) => {
      const rangeStart = api.value(0);
      const rangeEnd = api.value(1);
      const count = api.value(2);

      if (!count) return null;

      const bottomLeft = api.coord([rangeStart, 0]);
      const bottomRight = api.coord([rangeEnd, 0]);
      const topLeft = api.coord([rangeStart, count]);
      const barLeft = Math.min(bottomLeft[0], bottomRight[0]);
      const barWidth = Math.abs(bottomRight[0] - bottomLeft[0]);

      const rectShape = echarts.graphic.clipRectByRect(
        {
          x: barLeft,
          y: topLeft[1],
          width: barWidth,
          height: bottomLeft[1] - topLeft[1],
        },
        {
          x: params.coordSys.x,
          y: params.coordSys.y,
          width: params.coordSys.width,
          height: params.coordSys.height,
        },
      );

      if (!rectShape) return null;

      return {
        type: 'rect',
        shape: rectShape,
        style: api.style({ fill: BAR_COLOR }),
      };
    };

    return {
      tooltip: {
        trigger: 'item',
        borderColor: '#C7C7CC',
        formatter: params => {
          const [rangeStart, , count] = params.data.value ?? params.data;
          const labelEnd = params.data.labelEnd ?? rangeStart + 9;
          const marker = `<span style="display:inline-block;margin-left:35px;margin-right:10px;border-radius:10px;width:10px;height:10px;background-color:${BAR_COLOR};"></span>`;
          return `${labelEnd} ~ ${rangeStart} dBm<br/>${marker}${count}`;
        },
      },
      grid: {
        left: CHART_MARGIN.left,
        right: CHART_MARGIN.right,
        top: 20,
        bottom: 45,
        show: true,
        borderColor: '#C7C7CC',
        borderWidth: 1,
      },
      xAxis: {
        type: 'value',
        min: X_AXIS_MIN,
        max: X_AXIS_MAX,
        inverse: true,
        interval: 10,
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: true, lineStyle: { color: '#C7C7CC' } },
        axisLabel: {
          color: '#8E8E93',
          fontSize: 12,
          margin: 12,
          formatter: value => (RSSI_TICKS.includes(value) ? `${value} dBm` : ''),
        },
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: isAllZero ? 50 : undefined,
        interval: isAllZero ? 10 : undefined,
        minInterval: 1,
        boundaryGap: ['0%', '30%'],
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: true, lineStyle: { type: 'dashed', color: '#C7C7CC' } },
        axisLabel: {
          color: '#8E8E93',
          fontSize: 12,
          margin: `${safeData?.[safeData?.length - 1]?.count === 0 ? 10 : 30}`,
          formatter: value => `${Math.round(value)}`,
        },
      },
      series: [
        {
          type: 'custom',
          renderItem,
          encode: {
            x: [0, 1],
            y: 2,
          },
          data: seriesData,
          itemStyle: { color: BAR_COLOR },
          markLine: {
            symbol: ['none', 'none'],
            label: { show: false },
            lineStyle: {
              color: '#172664',
              type: 'solid',
              width: 2,
            },
            data: [{ xAxis: selectedIndex }],
            silent: true,
            animation: false,
          },
          markPoint: {
            symbol: 'triangle',
            symbolSize: 12,
            symbolRotate: 180,
            symbolOffset: [0, -6],
            itemStyle: {
              color: '#DB0046',
              borderColor: '#DB0046',
            },
            label: { show: false },
            data: [
              {
                xAxis: selectedIndex,
                yAxis: axisTopForMark,
              },
            ],
            silent: true,
            animation: false,
          },
        },
      ],
    };
  }, [isAllZero, axisTopForMark, seriesData, selectedIndex, safeData]);

  useEffect(() => {
    if (!chartInstance.current) return;
    chartInstance.current.setOption(option);
    if (isAllZero) return;
    const chart = chartInstance.current;
    const id = requestAnimationFrame(() => {
      try {
        const extent = chart.getModel().getComponent('yAxis', 0)?.axis?.scale?.getExtent?.();
        if (!extent || !Number.isFinite(extent[1])) return;
        const yMax = extent[1];
        setAxisTopForMark(prev => (Math.abs(prev - yMax) < 0.05 ? prev : yMax));
      } catch {
        // ignore
      }
    });
    return () => cancelAnimationFrame(id);
  }, [option, isAllZero]);

  useEffect(() => {
    const chart = echarts.init(chartRef.current);
    chartInstance.current = chart;
    chart.setOption(option);

    const id = requestAnimationFrame(() => {
      try {
        if (isAllZeroRef.current) return;
        const extent = chart.getModel().getComponent('yAxis', 0)?.axis?.scale?.getExtent?.();
        if (!extent || !Number.isFinite(extent[1])) return;
        const yMax = extent[1];
        setAxisTopForMark(prev => (Math.abs(prev - yMax) < 0.05 ? prev : yMax));
      } catch {
        // ignore
      }
    });

    const resize = () => chart.resize();
    window.addEventListener('resize', resize);
    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener('resize', resize);
      chart.dispose();
      chartInstance.current = null;
    };
  }, []);

  useEffect(() => {
    if (seriesData?.length > 0) {
      const targetValue = Number.isFinite(targetThreshold) ? targetThreshold : SLIDER_MIN;
      const nextSelectedRssi = Math.min(Math.max(targetValue, SLIDER_MIN), SLIDER_MAX);
      setSelectedIndex(nextSelectedRssi);
    }
  }, [targetThreshold, seriesData, setSelectedIndex]);

  return (
    <Box sx={{ width: '100%' }}>
      <style>
        {`
          .custom-slider {
            -webkit-appearance: none;
            width: 100%;
            background: transparent;
            outline: none;
            direction: rtl;
          }
          /* Track Style */
          .custom-slider::-webkit-slider-runnable-track {
            width: 100%;
            height: 4px;
            cursor: pointer;
            background: transparent;
            border-radius: 2px;
          }
          /* Thumb Style */
          .custom-slider::-webkit-slider-thumb {
            height: 20px;
            width: 20px;
            border-radius: 50%;
            background: #FFFFFF;
            border: 3px solid #1A2B56;
            cursor: pointer;
            -webkit-appearance: none;
            margin-top: -8px; /* Vertical Centering: (track height/2 - thumb height/2) */
            box-shadow: 0 1px 3px rgba(0,0,0,0.2);
          }
          .custom-slider::-moz-range-track {
            width: 100%;
            height: 4px;
            background: transparent;
            border-radius: 2px;
          }
          .custom-slider::-moz-range-thumb {
            height: 20px;
            width: 20px;
            border-radius: 50%;
            background: #FFFFFF;
            border: 3px solid #172664;
            cursor: pointer;
            box-shadow: 0 1px 3px rgba(0,0,0,0.2);
          }
        `}
      </style>

      <Box ref={chartRef} sx={{ height: 260, width: '100%' }} />

      <Box sx={{ marginLeft: '40px', marginRight: '30px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex' }}>
            <Typography style={{ fontWeight: 'bold' }}>Weak RSSI Threshold</Typography>
          </Box>
          <Typography style={{ fontWeight: 'bold' }}>{currentRssiText}</Typography>
        </Box>
        <input
          type="range"
          className="custom-slider"
          style={{
            background: `linear-gradient(to right, #172664 0%, #172664 ${sliderFillStart}%, #E5E5EA ${sliderFillStart}%, #E5E5EA 100%)`,
            borderRadius: '2px',
            height: '4px',
          }}
          step="1"
          min={SLIDER_MIN}
          max={SLIDER_MAX}
          value={selectedIndex}
          onChange={e => setSelectedIndex(Number(e.target.value))}
        />
      </Box>
    </Box>
  );
}
